/**
 * How a session's place in Needs you (the one selector, ADR 0056) reads in a row, a pill and a
 * filter. Pure, so it is tested directly.
 *
 * - blocked: Needs you (amber), as the session status says.
 * - asked: Asked (neutral, the question icon).
 * - open: an asked question older than a day reads "Open question" and files under Idle.
 * - set-aside: a dismissed or snoozed question reads Idle.
 */
import type { NeedsYouSessionLevel, Session, SessionStatus } from '@orgus/protocol';
import type { RowStatusProps } from '@orgus/ui';

type RowLook = Pick<RowStatusProps, 'tone' | 'icon' | 'label'>;

export const ASKED_LABEL = 'Asked';
export const OPEN_QUESTION_LABEL = 'Open question';

/** The status a session files under: a waiting session nobody owes an answer reads idle. */
export function shownStatus(s: Pick<Session, 'id' | 'status'>, levels?: ReadonlyMap<string, NeedsYouSessionLevel>): SessionStatus {
  if (s.status !== 'waiting' || !levels) return s.status;
  const l = levels.get(s.id);
  return l === 'open' || l === 'set-aside' ? 'idle' : s.status;
}

/**
 * Sessions as the quiet surfaces count them (the agent field, the orchestration flow, the fleet,
 * live counts): waiting only while blocked; a turn that ended on a question, an open question or
 * a set-aside one reads idle there, so amber means only what blocks (ADR 0056). Without levels
 * the list is returned as it is.
 */
export function blockedOnly<T extends Pick<Session, 'id' | 'status'>>(sessions: readonly T[], levels?: ReadonlyMap<string, NeedsYouSessionLevel>): T[] {
  if (!levels) return [...sessions];
  return sessions.map((s) => (s.status === 'waiting' && levels.get(s.id) !== 'blocked' ? { ...s, status: 'idle' as const } : s));
}

/** A waiting session's row status for its level; anything else keeps `base`. */
export function rowStatusForLevel(status: SessionStatus, level: NeedsYouSessionLevel | null | undefined, base: RowLook): RowLook {
  if (status !== 'waiting') return base;
  if (level === 'asked') return { tone: 'neutral', icon: 'asked', label: ASKED_LABEL };
  if (level === 'open') return { tone: 'neutral', icon: 'asked', label: OPEN_QUESTION_LABEL };
  if (level === 'set-aside') return { tone: 'neutral', icon: 'idle', label: 'Idle' };
  return base;
}

/** The drawer pill's label for a waiting session's level, or null to keep the status's own. */
export function pillLabelForLevel(level: NeedsYouSessionLevel | null | undefined): string | null {
  if (level === 'asked') return ASKED_LABEL;
  if (level === 'open') return OPEN_QUESTION_LABEL;
  if (level === 'set-aside') return 'Idle';
  return null;
}
